import React, { useState, useEffect, useCallback } from "react";
import { X, Check, Loader2, Settings, RotateCcw } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Per-employee access override modal.
 * Admin picks the exact list of dashboard tabs this user can see.
 * Override wins over the default role-based tabs on next login.
 *
 * Props:
 *   open: bool
 *   manager: { username, name, role, allowed_tabs_override? }
 *   authAxios
 *   onClose(): void
 *   onSaved(): void  (refresh list after save)
 */
const EXTRA_PERMISSIONS = [
  {
    key: "bookings.capacity.manage",
    label: "Manage Booking Capacity",
    desc: "Can change the global daily booking cap and per-date overrides.",
  },
];

const ManageAccessModal = ({ open, manager, authAxios, onClose, onSaved }) => {
  const [tabs, setTabs] = useState([]);
  const [selected, setSelected] = useState([]);
  const [roleDefault, setRoleDefault] = useState([]);
  const [extraPerms, setExtraPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasOverride, setHasOverride] = useState(false);

  const load = useCallback(async () => {
    if (!manager) return;
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/designations`);
      setTabs(r.data.available_tabs || []);
      const match = (r.data.designations || []).find((d) => d.key === manager.role);
      const defaultTabs = match?.tabs || [];
      setRoleDefault(defaultTabs);
      const override = manager.allowed_tabs_override;
      if (Array.isArray(override) && override.length) {
        setSelected(override);
        setHasOverride(true);
      } else {
        setSelected(defaultTabs);
        setHasOverride(false);
      }
      setExtraPerms(Array.isArray(manager.extra_permissions) ? manager.extra_permissions : []);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [authAxios, manager]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = (t) => setSelected((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));
  const togglePerm = (p) => setExtraPerms((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));

  const save = async () => {
    setSaving(true);
    try {
      await authAxios.put(`${API_URL}/api/admin/managers/${manager.username}/access`, {
        allowed_tabs: selected,
        extra_permissions: extraPerms,
      });
      onSaved && onSaved();
      onClose();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to save access");
    }
    setSaving(false);
  };

  const resetToRoleDefault = () => setSelected(roleDefault);

  if (!open || !manager) return null;

  return (
    <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="manage-access-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="px-5 py-4 border-b flex items-start justify-between bg-gradient-to-r from-[#1E1B4B] to-[#E83E8C] text-white">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Settings size={18} />
            </div>
            <div>
              <h3 className="font-bold text-base">Manage Access</h3>
              <p className="text-xs text-white/80">@{manager.username} · {manager.name}</p>
              <p className="text-[10px] text-white/60 mt-0.5">
                {hasOverride ? "Custom override active" : `Using ${manager.role} default`}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X size={20} /></button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 size={22} className="animate-spin text-[#E83E8C]" />
          </div>
        ) : (
          <>
            <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between text-xs">
              <span className="text-[#1E1B4B] font-semibold">
                {selected.length} of {tabs.length} tabs enabled
              </span>
              <button
                onClick={resetToRoleDefault}
                className="flex items-center gap-1 text-[#E83E8C] font-semibold hover:underline"
                data-testid="reset-to-role-default"
              >
                <RotateCcw size={11} /> Reset to role default
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {tabs.map((t) => {
                  const isOn = selected.includes(t);
                  const isRoleDefault = roleDefault.includes(t);
                  return (
                    <label
                      key={t}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer text-xs transition ${
                        isOn
                          ? "border-[#E83E8C] bg-[#FFF0F6] text-[#E83E8C] font-semibold"
                          : "border-gray-200 text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={() => toggle(t)}
                        className="accent-[#E83E8C]"
                        data-testid={`access-checkbox-${t}`}
                      />
                      <span className="truncate">{t}</span>
                      {isRoleDefault && (
                        <span className="ml-auto text-[8px] px-1 py-0.5 rounded bg-blue-100 text-blue-700">role</span>
                      )}
                    </label>
                  );
                })}
              </div>

              <div className="mt-5">
                <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-2">
                  Extra permissions
                </p>
                <div className="space-y-1.5">
                  {EXTRA_PERMISSIONS.map((p) => {
                    const isOn = extraPerms.includes(p.key);
                    return (
                      <label
                        key={p.key}
                        className={`flex items-start gap-2 px-3 py-2 rounded-xl border cursor-pointer text-xs transition ${
                          isOn
                            ? "border-indigo-400 bg-indigo-50 text-indigo-700"
                            : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                        data-testid={`extra-perm-label-${p.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={isOn}
                          onChange={() => togglePerm(p.key)}
                          className="accent-indigo-500 mt-0.5"
                          data-testid={`extra-perm-${p.key}`}
                        />
                        <div className="flex-1">
                          <p className="font-semibold">{p.label}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{p.desc}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-xs rounded-xl border border-gray-200 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving || selected.length === 0}
                className="px-4 py-2 text-xs rounded-xl bg-[#E83E8C] text-white font-bold hover:bg-[#c22d78] disabled:opacity-40 flex items-center gap-1.5"
                data-testid="save-access-btn"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                {saving ? "Saving…" : "Save Access"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ManageAccessModal;
